import cn from 'classnames'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Switch } from 'core-components/library/checkbox'
import { ReactComponent as TrainingIcon } from 'partner360/components/trainingCentre/assets/library.svg'
import { TrainingCentre } from 'partner360/components/trainingCentre/TrainingCentre'
import { TRANSLATION_KEYS as TRAINING_CENTRE_KEYS } from 'partner360/components/trainingCentre/translations'
import { ReactComponent as TourIcon } from 'partner360/components/widgetTour/assets/widget-tour-compass.svg'
import { useSwitchViewMode } from 'partner360/hooks/useSwitchViewMode'
import {
  setPlanSectionsDockEnabled,
  usePlanSectionsDockEnabled,
} from 'partner360/lib/planSectionsDockPreference'
import { useIsNetworkDebugUser } from 'shared/lib/networkDebug'

import { PLANNING_COCKPIT_TOUR_TRANSLATION_KEYS } from '../../../../configs/planningCockpitTourTranslations'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { formatRecalcDate } from '../../lib/formatRecalcDate'
import { BracesIcon, DockIcon, ReadyIcon, RecalcIcon, ViewIcon } from '../../lib/icons'
import type { ICockpitHelpRecalc, ICockpitHelpTour, TCockpitHelpMode } from '../../types'
import { HelpPanelAccess } from '../helpPanelAccess'
import { HelpPanelCard } from '../helpPanelCard'
import { ClassicHelpOfferings, CockpitHelpOfferings } from '../helpPanelOfferings'
import { HelpPanelRow } from '../helpPanelRow'

import styles from './HelpPanelContent.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.helpPanel

interface IHelpPanelContentProps {
  partnerId?: string
  mode: TCockpitHelpMode
  tour?: ICockpitHelpTour
  onClose: () => void
  /** Opens the Super Admin's window over this session's network data. */
  onOpenNetworkData?: () => void
  /** The WorldWide user's recalculation of their stored manager cockpit; absent for everyone else. */
  recalc?: ICockpitHelpRecalc
}

/** What the panel holds: help first, then the partner's offerings, then the cockpit
 *  itself. On the classic page every part but the way back to the new cockpit is greyed. The
 *  manager cockpit has no partner of its own: it holds the user's access, then the help, then for a
 *  WorldWide user the recalculation of their stored cockpit. A Super
 *  Admin also gets the network data, last. */
export const HelpPanelContent = ({
  partnerId,
  mode,
  tour,
  onClose,
  onOpenNetworkData,
  recalc,
}: IHelpPanelContentProps) => {
  const isNetworkDebugUser = useIsNetworkDebugUser()
  const switchViewMode = useSwitchViewMode()
  const isSectionsDockEnabled = usePlanSectionsDockEnabled()
  const t = useSafetyTranslation()
  const isClassic = mode === 'classic'
  const isPartnerCockpit = mode !== 'manager'

  const tourCard = (
    <HelpPanelCard
      icon={<TourIcon />}
      title={
        <Translation
          text={tour?.labelKey ?? PLANNING_COCKPIT_TOUR_TRANSLATION_KEYS.tourButton}
          span
        />
      }
      text={<Translation text={KEYS.tourText} span />}
      disabled={isClassic || !tour?.isAvailable || tour.isActive}
      onClick={() => {
        onClose()
        tour?.onStart()
      }}
    />
  )

  const trainingCard = (disabled: boolean) => (
    <HelpPanelCard
      icon={<TrainingIcon viewBox="0 0 24 24" />}
      title={<Translation text={TRAINING_CENTRE_KEYS.btnText} span />}
      text={<Translation text={KEYS.trainingText} span />}
      disabled={disabled}
    />
  )

  const helpCards = (
    <div className={styles.cards}>
      {tourCard}
      {isClassic ? (
        trainingCard(true)
      ) : (
        <TrainingCentre renderTrigger={({ disabled }) => trainingCard(disabled)} />
      )}
    </div>
  )

  return (
    <>
      {isClassic && (
        <p className={styles.note}>
          <Translation text={KEYS.classicNote} span />
        </p>
      )}

      {!isPartnerCockpit && (
        <section className={styles.section}>
          <h3 className={styles.caption}>
            <Translation text={KEYS.access} span />
          </h3>
          <HelpPanelAccess />
        </section>
      )}

      {isPartnerCockpit ? (
        helpCards
      ) : (
        <section className={styles.section}>
          <h3 className={styles.caption}>
            <Translation text={KEYS.help} span />
          </h3>
          {helpCards}
        </section>
      )}

      {!isPartnerCockpit && recalc && (
        <section className={styles.section}>
          <h3 className={styles.caption}>
            <Translation text={KEYS.cockpit} span />
          </h3>
          {recalc.readyAt ? (
            <HelpPanelRow
              icon={<ReadyIcon className={styles.ready} />}
              title={
                <span className={styles.readyTitle}>
                  <Translation text={KEYS.recalcReady} span />
                </span>
              }
              subtitle={<Translation text={KEYS.recalcReadyText} span />}
              onClick={() => {
                onClose()
                recalc.onRefresh()
              }}
            />
          ) : (
            <HelpPanelRow
              icon={<RecalcIcon className={cn({ [styles.spinning]: recalc.isRunning })} />}
              title={<Translation text={KEYS.recalc} span />}
              subtitle={
                recalc.requestedAt ? (
                  <Translation
                    text={recalc.isRunning ? KEYS.recalcRunning : KEYS.recalcLast}
                    replaceParams={[{ key: '{date}', value: formatRecalcDate(recalc.requestedAt) }]}
                    span
                  />
                ) : (
                  <Translation text={KEYS.recalcText} span />
                )
              }
              disabled={recalc.isRunning}
              onClick={recalc.onRequest}
            />
          )}
        </section>
      )}

      {isPartnerCockpit && (
        <>
          <section className={styles.section}>
            {isClassic ? (
              <ClassicHelpOfferings />
            ) : (
              partnerId && <CockpitHelpOfferings partnerId={partnerId} />
            )}
          </section>

          <section className={styles.section}>
            <h3 className={styles.caption}>
              <Translation text={KEYS.cockpit} span />
            </h3>
            {!isClassic && (
              <HelpPanelRow
                icon={<DockIcon />}
                title={<Translation text={KEYS.sectionsDock} span />}
                subtitle={<Translation text={KEYS.sectionsDockText} span />}
                end={
                  <span aria-label={t(KEYS.sectionsDock)}>
                    <Switch
                      checked={isSectionsDockEnabled}
                      onChange={setPlanSectionsDockEnabled}
                      size="sm"
                    />
                  </span>
                }
              />
            )}
            <HelpPanelRow
              icon={<ViewIcon />}
              title={<Translation text={isClassic ? KEYS.toNew : KEYS.toClassic} span />}
              subtitle={<Translation text={isClassic ? KEYS.toNewText : KEYS.toClassicText} span />}
              onClick={() => {
                onClose()
                switchViewMode(isClassic ? 'new' : 'old')
              }}
            />
          </section>
        </>
      )}

      {isNetworkDebugUser && onOpenNetworkData && (
        <section className={styles.section}>
          <h3 className={styles.caption}>
            <Translation text={KEYS.superAdmin} span />
          </h3>
          <HelpPanelRow
            icon={<BracesIcon />}
            title={<Translation text={KEYS.networkData} span />}
            subtitle={<Translation text={KEYS.networkDataText} span />}
            onClick={() => {
              onClose()
              onOpenNetworkData()
            }}
          />
        </section>
      )}
    </>
  )
}

HelpPanelContent.displayName = 'HelpPanelContent'
